/**
 * Pure helpers behind `CurrencyInput` (Complete_app §15.2, §17.1 "appropriate
 * currency inputs"). Western grouping (1,234,567.50); the currency is never
 * assumed — a null currency shows a bare number.
 *
 * Global desk: the India desk's copy groups INR as 12,34,567 and reads it back
 * in lakh/crore. Neither belongs on global-lexvio.ai (CLAUDE.md §1A), so here
 * every currency groups in threes and the read-back is compact notation.
 */
import { formatCompactMoney } from '@/lib/format';

/** Strip grouping and symbols; '' when nothing numeric is left. Keeps one '.' and up to 2 decimals. */
export function normaliseAmountInput(raw: string): string {
  let s = raw.replace(/[^\d.]/g, '');
  const dot = s.indexOf('.');
  if (dot !== -1) {
    s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, '').slice(0, 2);
  }
  s = s.replace(/^0+(?=\d)/, '');
  return s;
}

/** '' → null; otherwise the number. */
export function parseAmount(raw: string): number | null {
  const n = normaliseAmountInput(raw);
  if (n === '' || n === '.') return null;
  const v = Number(n);
  return Number.isFinite(v) ? v : null;
}

/** Group the integer part in threes: 1,234,567. `currency` is kept for API parity. */
export function groupAmount(normalised: string, _currency?: string | null): string {
  if (!normalised) return '';
  const [intPart, dec] = normalised.split('.');
  const digits = intPart || '0';
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return dec !== undefined ? `${grouped}.${dec}` : grouped;
}

const SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  AED: 'AED',
  SGD: 'S$',
  AUD: 'A$',
  CAD: 'C$',
  NZD: 'NZ$',
  HKD: 'HK$',
  JPY: '¥',
  CHF: 'CHF',
  ZAR: 'R',
  KES: 'KSh',
  NGN: '₦',
};

export function currencySymbol(currency: string | null | undefined): string | null {
  if (!currency) return null;
  return SYMBOLS[currency] ?? currency;
}

/**
 * "$12.5M" under a money field once the amount reaches 10,000, so a person
 * can check the zeros. Null below that or with no currency.
 */
export function amountInWords(
  value: number | null,
  currency: string | null | undefined,
  locale?: string,
): string | null {
  if (value === null || !currency || value < 10_000) return null;
  return formatCompactMoney(value, currency, { locale });
}
